import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import clsx from 'clsx';
import { Collapse, FormControl, IconButton, makeStyles, MenuItem, Select, TextField, Typography } from '@material-ui/core';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import ErrorOutline from '@material-ui/icons/ErrorOutline';
import ExpandMore from '@material-ui/icons/ExpandMore';
import TextFields from '@material-ui/icons/TextFields';
import { removeEditorItemAndSync, setEditorSelectedId, updateEditorItem } from '../../Reducers/Configurator/Actions';
import {
  getEditorSelectedId,
  getFonts,
  getOptionsForClassification,
  OptionClassificationIdentifiers,
} from '../../Reducers/Configurator/Selectors';
import type { TextItem } from '../../Reducers/Configurator/types';
import { XXL_COLORS } from '../../theme/colors';
import { getFontFamilyName } from '../../utils/fonts';
import { editorSidebarPaperProps } from '../Editor/useDeselectEditorOnClickAway';
import ComponentOptionsSection from './ComponentOptionsSection';
import SizeSection from './SizeSection';
import { useItemColorConflict } from './useItemColorConflict';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';

const useStyles = makeStyles((theme) => {
  const tokens = (theme as { xxlprint?: typeof XXL_COLORS }).xxlprint || XXL_COLORS;
  return {
    card: {
      border: `1px solid ${tokens.borderStrong}`,
      marginBottom: theme.spacing(1.25),
      overflow: 'hidden',
      backgroundColor: tokens.paperBackground,
      transition: 'border-color 0.12s ease, box-shadow 0.12s ease',
    },
    cardOpen: {
      borderColor: tokens.accent,
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      padding: theme.spacing(1.25, 1.5),
      cursor: 'pointer',
      backgroundColor: tokens.paperBackground,
      userSelect: 'none',
      transition: 'background-color 0.12s ease',
      '&:hover': { backgroundColor: tokens.surfaceMuted },
    },
    headerOpen: {
      borderBottom: `1px solid ${tokens.borderLight}`,
      backgroundColor: tokens.selectedTint,
      '&:hover': { backgroundColor: tokens.selectedTint },
    },
    colorDot: {
      width: 12,
      height: 12,
      borderRadius: '50%',
      flexShrink: 0,
      border: `1px solid rgba(0,0,0,0.15)`,
    },
    typeIcon: {
      flexShrink: 0,
      color: tokens.textBase,
    },
    summary: {
      flex: 1,
      minWidth: 0,
    },
    summaryText: {
      fontSize: 13,
      fontWeight: 600,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      lineHeight: 1.3,
    },
    summaryMeta: {
      fontSize: 11,
      color: tokens.textBase,
      marginTop: 2,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    expandIcon: {
      flexShrink: 0,
      color: tokens.textBase,
      transition: 'transform 0.2s ease',
    },
    expandIconOpen: {
      transform: 'rotate(180deg)',
    },
    deleteButton: {
      flexShrink: 0,
      padding: 4,
      color: tokens.textBase,
      '&:hover': { color: theme.palette.error.main },
    },
    warningIcon: {
      flexShrink: 0,
      color: theme.palette.error.main,
    },
    body: {
      padding: theme.spacing(1.5),
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.5),
    },
    fieldLabel: {
      fontSize: 13,
      fontWeight: 700,
      marginBottom: 6,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      color: tokens.sectionTitle,
    },
    fontValue: {
      fontSize: 16,
      lineHeight: 1.2,
    },
    fontMenuPaper: {
      maxHeight: 360,
      border: `1px solid ${tokens.borderStrong}`,
      borderRadius: 0,
    },
    fontMenuItem: {
      fontSize: 16,
    },
    // Off-screen render of every font so all faces fetch upfront — makes the
    // dropdown previews instant and is the real upfront work the loader covers.
    fontPreload: {
      position: 'absolute',
      left: -99999,
      top: 0,
      fontSize: 12,
      whiteSpace: 'nowrap',
      pointerEvents: 'none',
    },
  };
});

type Props = { item: TextItem };

export default function TextItemCard({ item }: Props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const selectedId = useSelector(getEditorSelectedId);
  const fonts = useSelector(getFonts);
  const surfaceOptions = useSelector(getOptionsForClassification(OptionClassificationIdentifiers.surface));

  const isOpen = item.id === selectedId;
  const [editingText, setEditingText] = React.useState(item.content);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Sync local text only when the card opens — preserves caret position while typing
  React.useEffect(() => {
    if (isOpen) setEditingText(item.content);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const { code: urlCode } = useParams<{ code?: string }>();

  // Focus the text input after the Collapse animation finishes (200ms) when the card opens.
  // Select all text on identifier loads so the first keystroke naturally replaces the default
  // content. Skip select-all on code loads — the text was intentionally saved.
  React.useEffect(() => {
    if (!isOpen) return undefined;
    const timer = setTimeout(() => {
      inputRef.current?.focus();
      if (!urlCode) inputRef.current?.select();
    }, 210);
    return () => clearTimeout(timer);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const { hasConflict } = useItemColorConflict(item);

  const itemSurfaceId = item.selectedOptions?.[OptionClassificationIdentifiers.surface];
  const surfaceName = surfaceOptions.find((o) => o.identifier === itemSurfaceId)?.title ?? '';

  const handleHeaderClick = () => {
    dispatch(setEditorSelectedId(isOpen ? null : item.id));
  };

  const handleTextChange = (value: string) => {
    setEditingText(value);
    dispatch(updateEditorItem(item.id, { content: value }));
  };

  const handleFontSelect = (fontFamily: string) => {
    dispatch(updateEditorItem(item.id, { fontFamily }));
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(removeEditorItemAndSync(item.id));
  };

  return (
    <div className={clsx(classes.card, isOpen && classes.cardOpen)}>
      <div
        className={clsx(classes.header, isOpen && classes.headerOpen)}
        onClick={handleHeaderClick}
      >
        <TextFields className={classes.typeIcon} fontSize="small" />
        <span className={classes.colorDot} style={{ backgroundColor: item.color }} aria-hidden />
        <div className={classes.summary}>
          <div className={classes.summaryText}>
            {item.content || t('textItemCard.emptyLabel')}
          </div>
          <div className={classes.summaryMeta}>
            {[item.fontFamily?.replace(/_/g, ' '), surfaceName].filter(Boolean).join(' · ')}
          </div>
        </div>
        {hasConflict && (
          <ErrorOutline
            className={classes.warningIcon}
            fontSize="small"
            aria-label={t('textItemCard.colorConflictBadgeLabel')}
          />
        )}
        <ExpandMore
          className={clsx(classes.expandIcon, isOpen && classes.expandIconOpen)}
          fontSize="small"
        />
        <IconButton className={classes.deleteButton} size="small" onClick={handleDelete} aria-label={t('common.delete')}>
          <DeleteOutline fontSize="small" />
        </IconButton>
      </div>

      <Collapse in={isOpen} timeout={200} unmountOnExit>
        <div className={classes.body}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            value={editingText}
            onChange={(e) => handleTextChange(e.target.value)}
            placeholder={t('textItemCard.placeholder')}
            inputRef={inputRef}
          />

          <Typography className={classes.fieldLabel}>{t('textItemCard.fontLabel')}</Typography>
          <FormControl variant="outlined" size="small" fullWidth>
            <Select
              value={item.fontFamily || ''}
              onChange={(e) => handleFontSelect(e.target.value as string)}
              renderValue={(val) => {
                const selected = fonts.find((f) => f.name === val);
                return (
                  <span
                    className={classes.fontValue}
                    style={{ fontFamily: selected ? getFontFamilyName(selected) : undefined }}
                  >
                    {String(val).replace(/_/g, ' ')}
                  </span>
                );
              }}
              MenuProps={{ PaperProps: editorSidebarPaperProps({ className: classes.fontMenuPaper }) }}
            >
              {fonts.map((font) => (
                <MenuItem
                  key={font.name}
                  value={font.name}
                  className={classes.fontMenuItem}
                  style={{ fontFamily: getFontFamilyName(font) }}
                >
                  {font.name.replace(/_/g, ' ')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <div className={classes.fontPreload} aria-hidden>
            {fonts.map((font) => (
              <span key={font.name} style={{ fontFamily: getFontFamilyName(font) }}>
                {font.name}
              </span>
            ))}
          </div>

          <ComponentOptionsSection classificationId={OptionClassificationIdentifiers.surface} />
          <ComponentOptionsSection classificationId={OptionClassificationIdentifiers.color} showColorSwatch />
          <SizeSection />
        </div>
      </Collapse>
    </div>
  );
}
